import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { tap, map, finalize, filter } from 'rxjs/operators';
import { isEmpty } from 'lodash';

import { AppState } from '../../store/state/app.state';
import { environment } from 'projects/workspace/src/environments/environment';
import { PaginationModel } from 'common/src/models';
import { ResponseList, ResponseModel } from '../../shared/models';
import { FilterModelNew } from '../../outgoing-invoice/models/filter-model-new';
import { selectCompanyProfile } from '../../administration/store/selectors';
import { CompanyProfile } from '../../administration/models/company-profile.model';
import { VatReportStatusEnum } from '../enums';
import { VatReportEntityModel, VatReportModel } from '../models';
import {
  DecrementLoadingRequestsCount,
  IncrementLoadingRequestsCount,
  LoadVatReport,
  LoadVatReportsList, UpdateShouldRefreshEntity,
  UpdateVatReportUpdatedAt
} from '../store/actions/vat-report.actions';
import { FileUploadParams } from '../../../../../../common/src/models/file-upload-params.model';

@Injectable()
export class VatReportsApiService {
  private wid: number;

  private apiUrl(url: string = ''): string {
    return `${environment.javaApiVersion}/workspaces/${this.wid}/reports` + url;
  }

  constructor(
    private readonly http: HttpClient,
    private readonly store: Store<AppState>
  ) {
    this.store.select(selectCompanyProfile)
      .pipe(filter(cp => !!cp && !isEmpty(cp)))
      .subscribe((profile: CompanyProfile) => {
        this.wid = profile.workspaceId;
      });
  }

  public getVatReportsList(
    reportStatus: VatReportStatusEnum,
    pagination: PaginationModel,
    sort: FilterModelNew,
  ): Observable<ResponseList<VatReportModel>> {

    const params = {
      reportStatus,
      page: pagination.page,
      sortBy: sort.sortBy,
      direction: sort.direction,
      length: pagination.per_page,
    };

    // for (const [key, value] of Object.entries(filters)) {
    //   params[key] = Array.isArray(value) ? value.join(',') : value.toString();
    // }

    return this.http.get<ResponseList<VatReportModel>>(this.apiUrl(), { params })
      .pipe(tap((data: ResponseList<VatReportModel>) => {
        this.store.dispatch(LoadVatReportsList({
          vatReportListData: {
            [data.pagination.page]: {
              pagination: data.pagination,
              sort,
              data: data.data
            }
          },
          status: reportStatus,
          page: data.pagination.page
        }));
      }));
  }

  public getVatReportsListCount(): Observable<number> {
    return this.http.get<ResponseModel<number>>(this.apiUrl('/count'))
      .pipe(map((response: ResponseModel<number>) => response.data));
  }

  createReport(report): Observable<any[]> {
    return this.http.post<ResponseModel<any[]>>(this.apiUrl(), report)
      .pipe(map((response: ResponseModel<any[]>) => response.data));
  }

  public getVatReportById(id: string): Observable<VatReportEntityModel> {
    this.store.dispatch(IncrementLoadingRequestsCount());

    return this.http.get<ResponseModel<VatReportEntityModel>>(this.apiUrl(`/${id}`))
      .pipe(
        tap((response: ResponseModel<VatReportEntityModel>) => {
          this.store.dispatch(LoadVatReport({ vatReport: response.data }));
          this.store.dispatch(UpdateVatReportUpdatedAt({ updatedAt: new Date() }));
          this.store.dispatch(UpdateShouldRefreshEntity({ isShouldRefresh: false }));
        }),
        map((response: ResponseModel<VatReportEntityModel>) => response.data),
        finalize(() => this.store.dispatch(DecrementLoadingRequestsCount()))
      );
  }

  public changeVatReportStatus(
    action: 'DELETE'|'PERMANENTLY_DELETE'|'SET_REPORTED'|'RESTORE_DELETED',
    ids: string[]
  ): Observable<{ requestedCount: number; succeededCount: number; failedCount: number }> {
    this.store.dispatch(IncrementLoadingRequestsCount());
      const params = new HttpParams()
        .set('action', action)
        .set('reportId', ids.join(','));

    return this.http.patch<ResponseModel<{ requestedCount: number; succeededCount: number; failedCount: number }>>(this.apiUrl(), params)
      .pipe(
        tap(() => {
          this.store.dispatch(UpdateVatReportUpdatedAt({ updatedAt: new Date() }));
          this.store.dispatch(UpdateShouldRefreshEntity({ isShouldRefresh: false }));
        }),
        map((response: ResponseModel<{ requestedCount: number; succeededCount: number; failedCount: number }>) => response.data),
        finalize(() => this.store.dispatch(DecrementLoadingRequestsCount()))
      );
  }

  public getExportParams(id: string, format: 'PDF'|'XML'): Observable<FileUploadParams> {
    const fileParams: FileUploadParams = {
      url: `${environment.javaApiVersion}/workspaces/${this.wid}/reports/${id}/${format}`,
      type: 'zip',
    };
    return of(fileParams);
  }

}
